/**
 * UserMentionPopover - Popover de seleção de usuários para @menções
 *
 * Lista filtrada de membros do tenant exibida acima do textarea de comentário.
 * O filtro é controlado externamente via `searchTerm` (texto após o `@`) — não há
 * input interno para não disputar foco com o textarea (causava bug onde o clique
 * no item parecia "não fazer nada" e o `cursorPosition` ficava dessincronizado).
 *
 * @version 2.0.1
 * RELEASE: 2026-06-05 v2.0.1 - Fix clique do mouse dentro de Radix Dialog:
 *          adicionado `pointer-events-auto` no container (Portal herda
 *          `pointer-events: none` do body quando Dialog está aberto) e
 *          z-index elevado para z-[200] (acima de DialogContent z-50).
 *          Adicionado `cursor-pointer` nos itens.
 * RELEASE: 2026-06-05 v2.0.0 - Removido input interno de busca + foco automático.
 *          Itens com onMouseDown.preventDefault para preservar foco do textarea
 *          ao clicar. Filtro agora é 100% controlado pelo pai via `searchTerm`.
 * RELEASE: 2026-02-09 v1.2.0 - Portal + posicionamento dinâmico para evitar corte
 * RELEASE: 2026-02-09 v1.1.0 - Adicionado campo de pesquisa e scroll robusto
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

// Interface do usuário mencionável
interface MentionableUser {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

// Props do componente
interface UserMentionPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (user: MentionableUser) => void;
  /** Lista já filtrada pelo pai conforme o texto após o `@` */
  users: MentionableUser[];
  /** Texto após o `@` (apenas para mensagem "nenhum resultado") */
  searchTerm: string;
  /** Referência para posicionamento dinâmico via Portal */
  anchorRef?: React.RefObject<HTMLTextAreaElement>;
}

/**
 * Popover flutuante para seleção de usuários (sem input interno).
 * - Posiciona-se acima do textarea via Portal no body.
 * - Não rouba foco do textarea (essencial para não quebrar `selectionStart`).
 * - Navegação por teclado é tratada pelo textarea/pelo pai (Enter/Esc/setas).
 */
export function UserMentionPopover({
  isOpen,
  onClose,
  onSelect,
  users,
  searchTerm,
  anchorRef,
}: UserMentionPopoverProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Índice do item destacado (navegação por teclado)
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 288 });

  // Calcula posição dinâmica baseada no anchor (textarea)
  useEffect(() => {
    if (!isOpen || !anchorRef?.current) return;

    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) {
        setPosition({
          top: rect.top - 8,
          left: rect.left,
          width: Math.max(288, rect.width),
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, anchorRef]);

  // Reset índice quando a lista filtrada muda ou o popover abre
  useEffect(() => {
    setSelectedIndex(0);
  }, [users.length, isOpen]);

  // Auto-scroll para item selecionado
  useEffect(() => {
    if (itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  // Keyboard navigation (escuta global enquanto aberto; textarea segue com foco)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < users.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          if (users[selectedIndex]) {
            e.preventDefault();
            onSelect(users[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, users, selectedIndex, onSelect, onClose]);

  // Click fora fecha. Ignoramos cliques no próprio popover e no textarea âncora.
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  // Conteúdo do popover
  const popoverContent = (
    <div
      ref={containerRef}
      className="fixed bg-popover border rounded-lg shadow-lg z-[200] overflow-hidden pointer-events-auto"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        transform: 'translateY(-100%)',
      }}
      // Impede que clicar no popover (área vazia/scrollbar) roube o foco do textarea
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* Header */}
      <div className="px-3 py-2 border-b bg-muted/30">
        <p className="text-xs font-medium text-muted-foreground">
          Mencionar usuário {searchTerm && <span className="text-foreground">· "{searchTerm}"</span>}
        </p>
      </div>

      {/* Lista de usuários com scroll */}
      {users.length === 0 ? (
        <div className="p-3">
          <p className="text-xs text-muted-foreground text-center">
            {searchTerm
              ? `Nenhum usuário encontrado para "${searchTerm}"`
              : 'Nenhum usuário disponível'}
          </p>
        </div>
      ) : (
        <ScrollArea className="h-[200px]" axis="y">
          <div className="p-1">
            {users.map((user, index) => (
              <button
                key={user.id}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                type="button"
                // CRUCIAL: preventDefault no mousedown impede blur do textarea
                // antes do onClick rodar, mantendo selectionStart/foco consistentes.
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(user);
                }}
                onMouseEnter={() => setSelectedIndex(index)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors cursor-pointer',
                  'hover:bg-accent',
                  index === selectedIndex && 'bg-accent',
                )}
              >
                <SmartAvatar src={user.avatar_url} name={user.full_name} size="xs" />
                <span className="text-sm truncate">{user.full_name}</span>
              </button>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Footer com hint */}
      <div className="px-3 py-1.5 border-t bg-muted/30">
        <p className="text-[10px] text-muted-foreground">
          ↑↓ navegar • Enter selecionar • Esc fechar
        </p>
      </div>
    </div>
  );

  // Renderiza via Portal no body para escapar do overflow de containers pais
  return createPortal(popoverContent, document.body);
}
